import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function CorrectEntryDialog({ ts, index, onOpenChange, onDone }) {
  const e = index != null ? ts?.entries?.[index] : null;
  const [f, setF] = useState({});
  const [reason, setReason] = useState("");
  const [info, setInfo] = useState(null);
  const [sites, setSites] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!e) return;
    setF({ site_id: e.site_id || "", job_type: e.job_type || "", description: e.description || "", start_time: e.start_time, finish_time: e.finish_time, travel_minutes: e.travel_minutes || 0 });
    setReason("");
    Promise.all([api.get(`/timesheets/${ts.id}/correction-info`), api.get("/sites"), api.get("/job-types")])
      .then(([a, b, c]) => { setInfo(a.data); setSites(b.data); setJobTypes(c.data); }).catch(() => {});
  }, [e, ts?.id]);

  const save = async () => {
    setBusy(true);
    try {
      const changes = { ...f, travel_minutes: Number(f.travel_minutes || 0) };
      if (!changes.site_id) delete changes.site_id;
      if (!info?.times_allowed) { delete changes.start_time; delete changes.finish_time; delete changes.travel_minutes; }
      const { data } = await api.post(`/timesheets/${ts.id}/correct`, { index, changes, reason });
      toast.success("Timesheet corrected. The employee has been notified");
      onDone(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const times = info?.times_allowed;
  return (
    <Dialog open={!!e} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="correct-dialog">
        <DialogHeader>
          <DialogTitle>Correct approved entry</DialogTitle>
          <DialogDescription>{e?.date} · {e?.site_name || "No site"} · {e?.start_time}–{e?.finish_time}</DialogDescription>
        </DialogHeader>
        {info?.payroll_run && (
          <div data-testid="correct-payroll-warning" className="flex gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />Payroll has already been run for this week. You can fix the site, job type and description; hours and pay stay as paid.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Site</Label>
            <Select value={f.site_id} onValueChange={(v) => setF({ ...f, site_id: v })}>
              <SelectTrigger className="mt-1" data-testid="correct-site"><SelectValue placeholder="Pick a site" /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Job type</Label>
            <Select value={f.job_type} onValueChange={(v) => setF({ ...f, job_type: v })}>
              <SelectTrigger className="mt-1" data-testid="correct-job-type"><SelectValue placeholder="Pick a job type" /></SelectTrigger>
              <SelectContent>{[...new Set([f.job_type, ...jobTypes.map((j) => j.name)].filter(Boolean))].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Start</Label><Input type="time" disabled={!times} value={f.start_time || ""} onChange={(x) => setF({ ...f, start_time: x.target.value })} className="mt-1" data-testid="correct-start" /></div>
          <div><Label>Finish</Label><Input type="time" disabled={!times} value={f.finish_time || ""} onChange={(x) => setF({ ...f, finish_time: x.target.value })} className="mt-1" data-testid="correct-finish" /></div>
          <div><Label>Travel (minutes)</Label><Input type="number" min="0" disabled={!times} value={f.travel_minutes} onChange={(x) => setF({ ...f, travel_minutes: x.target.value })} className="mt-1" data-testid="correct-travel" /></div>
          <div className="col-span-2"><Label>Description</Label><Input value={f.description || ""} onChange={(x) => setF({ ...f, description: x.target.value })} className="mt-1" data-testid="correct-description" /></div>
          <div className="col-span-2"><Label>Reason for the correction *</Label><Textarea value={reason} onChange={(x) => setReason(x.target.value)} placeholder="e.g. Hours were booked to the wrong job" className="mt-1" data-testid="correct-reason" /></div>
        </div>
        {times && <p className="text-xs text-zinc-500">Changing times or the site recalculates pay and charge for this entry only. The rest of the week keeps its approved figures.</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="correct-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || reason.trim().length < 3} data-testid="correct-save">{busy ? "Saving…" : "Save correction"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
